import { defineMessages } from 'react-intl';

export default defineMessages({
	migrationUpdating: {
		defaultMessage: 'Updating...',
		description: 'Status shown on a help center card while its update is in progress.',
		id: 'help.center.card.migration.updating',
	},
	migrationProgress: {
		defaultMessage: '75%',
		description: 'Fixed visible progress shown while a help center update is in progress.',
		id: 'help.center.card.migration.progress',
	},
	migrationProgressAccessibleLabel: {
		defaultMessage: 'Updating... 75%',
		description: 'Accessible determinate progress label for an in-progress help center update.',
		id: 'help.center.card.migration.progress.accessible-label',
	},
	resumeMigration: {
		defaultMessage: 'View fullscreen',
		description: 'Action that restores the fullscreen view for an in-progress help center update.',
		id: 'help.center.card.migration.resume',
	},
	migrationActions: {
		defaultMessage: 'Help center actions',
		description:
			'Label for the help center actions button, unavailable while an update is in progress.',
		id: 'help.center.card.migration.actions',
	},
	migrationUpdatedOn: {
		defaultMessage: 'Last updated {date}',
		description: 'Last updated date on a help center card while its update is in progress.',
		id: 'help.center.card.migration.updated-on',
	},
	helpCenterGenericErrorDesc: {
		defaultMessage: 'Check your connection and try again.',
		description: 'Generic description message for help center error',
		id: 'help.center.card.update.error.description.generic',
	},
	helpCenterErrorDesc: {
		// eslint-disable-next-line @atlassian/i18n/no-useless-message
		defaultMessage: '{value}',
		description: 'Description message for help center error',
		id: 'help.center.card.update.error.description',
	},
	helpCenterDeleteErrorTitleText: {
		defaultMessage: 'We couldn’t delete ‘{value}’',
		description: 'Title message for delete help center error',
		id: 'help.center.delete.error.title.text',
	},
	helpCenterDeleteErrorDesc: {
		defaultMessage: 'Check your connection and try again.',
		description: 'Description message for delete help center error',
		id: 'help.center.delete.error.desc',
	},
	helpCenterDeleteSuccessTitleText: {
		defaultMessage: '‘{value}’ deleted',
		description: 'Delete help center success message',
		id: 'help.center.delete.success.title.text',
	},
	helpCenterUpdatedOn: {
		defaultMessage: 'Last updated: {date}',
		description:
			'Label showing when the help center was last updated; {date} is the formatted date',
		id: 'help.center.updated.on.text',
	},
	helpCenterCustomizeButtonText: {
		defaultMessage: 'Customize look and feel',
		description: 'Help center customize button text',
		id: 'help.center.customize.button.text',
	},
	helpCenterUpdateButtonText: {
		defaultMessage: 'Update Help Center',
		description: 'Action that starts updating a legacy help center to a help hub',
		id: 'help.center.card.migration.update.button',
	},
	helpCenterReviewUpdateButtonText: {
		defaultMessage: 'Review update',
		description: 'Action that opens the updated help center draft for review',
		id: 'help.center.card.update.review.button',
	},
	helpCenterEditWarningTitle: {
		defaultMessage: 'Edit current help center?',
		description: 'Title warning that changes to the current help center will not sync to its draft',
		id: 'help.center.card.migration.edit.warning.title',
	},
	helpCenterEditWarningDescription: {
		defaultMessage:
			'Changes you make here won’t sync to your new help center draft. We recommend reviewing and editing the draft instead.',
		description: 'Warning shown before editing a legacy help center that has a migrated draft',
		id: 'help.center.card.migration.edit.warning.description',
	},
	helpCenterEditWarningCancelButton: {
		defaultMessage: 'Cancel',
		description: 'Action that cancels editing the current help center',
		id: 'help.center.card.migration.edit.warning.cancel',
	},
	helpCenterEditWarningGoToDraftButton: {
		defaultMessage: 'Go to draft',
		description: 'Action that opens the migrated help center draft',
		id: 'help.center.card.migration.edit.warning.go.to.draft',
	},
	helpCenterEditWarningEditAnywayButton: {
		defaultMessage: 'Edit anyway',
		description: 'Action that continues editing the current help center despite the warning',
		id: 'help.center.card.migration.edit.warning.edit.anyway',
	},
	helpHubCustomizeButtonText: {
		defaultMessage: 'Customize',
		description: 'Help center customize button text',
		id: 'help.hub.customize.button.text',
	},
	helpCenterSettingsButtonText: {
		defaultMessage: 'Settings',
		description: 'Help center settings button text',
		id: 'help.center.settings.button.text',
	},
	helpCenterDeleteButtonText: {
		defaultMessage: 'Delete',
		description: 'Help center delete button text',
		id: 'help.center.delete.button.text',
	},
	helpCenterDeleteDialogTitle: {
		defaultMessage: 'Delete help center?',
		description: 'Help center delete dialog title',
		id: 'help.center.delete.dialog.title',
	},
	helpCenterDeleteDialogDesc: {
		// eslint-disable-next-line @atlassian/i18n/no-multiple-whitespaces
		defaultMessage: `‘{value}’  and any customizations you applied to it will be permanently deleted. This cannot be undone.`,
		description: 'Help center delete dialog description',
		id: 'help.center.delete.dialog.desc',
	},
	helpCenterDeleteDialogDeleteButton: {
		defaultMessage: 'Delete',
		description: 'Help center delete dialog delete button text',
		id: 'help.center.delete.dialog.delete.button',
	},
	helpCenterDeleteDialogCancelButton: {
		defaultMessage: 'Cancel',
		description: 'Help center delete dialog cancel button text',
		id: 'help.center.delete.dialog.cancel.button',
	},
	helpCenterDisabledDeleteButtonTooltip: {
		defaultMessage: `Your default help center can't be deleted.`,
		description: 'Help center disabled delete button tooltip text',
		id: 'help.center.disabled.delete.button.tooltip',
	},
	helpCenterDisabledMessagePopup: {
		defaultMessage: `You need to be on a Premium or Enterprise plan to access multiple help centers on your site.`,
		description: 'Help center disabled message to upgrade popup',
		id: 'help.center.disabled.message.popup',
	},
	defaultLozenge: {
		defaultMessage: 'Default',
		description: 'Lozenge for default help center',
		id: 'help.center.default.lozenge.message',
	},
	mappedProjectCount: {
		defaultMessage: '{count, plural, one {# linked project} other {# linked projects}}',
		description: 'Lozenge for mapped projects count',
		id: 'help.center.project.map.count',
	},
	mappedSpaceCountGalaxia: {
		defaultMessage: '{count, plural, one {# linked space} other {# linked spaces}}',
		description: 'Lozenge for mapped spaces count',
		id: 'help.center.project.map.count-galaxia',
	},
	mappedProjectCountAll: {
		defaultMessage: 'All projects linked',
		description: 'Lozenge indicating all projects are linked to the help center',
		id: 'help.center.project.map.all.count',
	},
	mappedSpaceCountAllGalaxia: {
		defaultMessage: 'All spaces linked',
		description: 'Lozenge indicating all spaces are linked to the help center',
		id: 'help.center.project.map.all.count-galaxia',
	},
	upgradeLozenge: {
		defaultMessage: 'Upgrade to access',
		description: 'Lozenge for upgrade to access help center',
		id: 'help.center.upgrade.lozenge.message',
	},
	helpCenterVirtualServiceAgentButtonText: {
		defaultMessage: 'Virtual service agent',
		description: 'Help center virtual service agent button text',
		id: 'help.center.virtual.service.agent.button.text',
	},
	changeBoardingDescription: {
		defaultMessage:
			'Manage help center details, link and unlink your projects, and track virtual service agent performance from here.',
		description: 'Onboarding tooltip body text for the help center management card',
		id: 'help.center.card.change.boarding.description',
	},
	changeBoardingDescriptionGalaxia: {
		defaultMessage:
			'Manage help center details, link and unlink your spaces, and track virtual service agent performance from here.',
		description: 'Onboarding tooltip body text for the help center card in Galaxia layout',
		id: 'help.center.card.change.boarding.description-galaxia',
	},
	changeBoardingTitle: {
		defaultMessage: 'Take charge of your help center',
		description: 'Heading text for the onboarding tooltip on the help center card',
		id: 'help.center.card.change.boarding.title',
	},
	changeBoardingButtonText: {
		defaultMessage: 'OK',
		description: 'Dismiss button label for the help center onboarding tooltip',
		id: 'help.center.card.change.boarding.button.description',
	},
});
